import { ReactNode, useId } from 'react';
import { Alert, Box, Chip, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/** Student Portal → Instructions: how to obtain and attest O/L and A/L
 * certificates. Static content, worded exactly as IMC supplied it — change
 * the text here only when IMC changes the instructions. */

const DOENETS_URL = 'https://certificate.doenets.lk./';
const MFA_APPOINTMENT_URL = 'https://share.google/LYnA1YHo0KBNyiz9V';

const ExternalLink = ({ href }: { href: string }) => (
  <Link
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      wordBreak: 'break-all',
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    {href}
    <OpenInNewIcon sx={{ fontSize: 16 }} aria-hidden />
  </Link>
);

const LabelledLink = ({ label, href }: { label: string; href: string }) => (
  <Typography variant="body2" sx={{ mt: 1 }}>
    <strong>{label}</strong> <ExternalLink href={href} />
  </Typography>
);

const Para = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const Lead = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" fontWeight={700} sx={{ mt: 1.5 }}>
    {children}
  </Typography>
);

const List = ({ ordered, children }: { ordered?: boolean; children: ReactNode }) => (
  <Box
    component={ordered ? 'ol' : 'ul'}
    sx={{ mt: 0.75, mb: 0, pl: 3, '& li': { mb: 0.75, lineHeight: 1.7, typography: 'body2' } }}
  >
    {children}
  </Box>
);

const Note = ({
  kind,
  children,
}: {
  kind: 'Note' | 'Important' | 'Important Reminder';
  children: ReactNode;
}) => (
  <Alert
    severity={kind === 'Note' ? 'info' : 'warning'}
    variant="outlined"
    sx={{ mt: 1.5, borderRadius: 2, '& .MuiAlert-message': { lineHeight: 1.7 } }}
  >
    <strong>{kind}:</strong> {children}
  </Alert>
);

/** A lettered subsection (A, B, C…). */
const Step = ({
  letter,
  title,
  children,
}: {
  letter: string;
  title: string;
  children: ReactNode;
}) => {
  const id = useId();
  return (
    <Box component="section" aria-labelledby={id} sx={{ py: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          aria-hidden
          sx={{
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {letter}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            id={id}
            variant="subtitle1"
            component="h4"
            fontWeight={700}
            sx={{ lineHeight: 1.4 }}
          >
            {letter}. {title}
          </Typography>
          {children}
        </Box>
      </Stack>
    </Box>
  );
};

/** A numbered main section (1. Local, 2. London). */
const Section = ({
  number,
  title,
  chip,
  children,
}: {
  number: number;
  title: string;
  chip: string;
  children: ReactNode;
}) => (
  <Paper
    component="section"
    variant="outlined"
    aria-labelledby={`section-${number}`}
    sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, borderTop: 4, borderTopColor: 'primary.main' }}
  >
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography
        id={`section-${number}`}
        variant="h6"
        component="h3"
        fontWeight={700}
        color="primary.main"
      >
        {number}. {title}
      </Typography>
      <Chip size="small" color="primary" variant="outlined" label={chip} />
    </Stack>
    <Stack divider={<Divider flexItem />}>{children}</Stack>
  </Paper>
);

export const PortalInstructionsTab = () => (
  <Stack spacing={2} sx={{ maxWidth: 900, mx: 'auto' }}>
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          <MenuBookIcon fontSize="large" />
        </Box>
        <Typography variant="h5" component="h2" fontWeight={800} sx={{ lineHeight: 1.3 }}>
          Instructions for Obtaining and Attesting O/L and A/L Certificates
        </Typography>
      </Stack>
    </Paper>

    <Section number={1} title="Local O/L and A/L Certificates" chip="Local">
      <Step
        letter="A"
        title="Original O/L and A/L Certificates Issued by the Department of Examinations, Sri Lanka"
      >
        <Lead>How to obtain the original O/L and A/L certificates:</Lead>
        <List>
          <li>
            Follow the link below to obtain your original O/L and A/L certificates from the
            Department of Examinations, Sri Lanka.
          </li>
        </List>
        <LabelledLink label="Link:" href={DOENETS_URL} />
      </Step>

      <Step
        letter="B"
        title="Original O/L and A/L Certificates attested by the Ministry of Foreign Affairs, Sri Lanka"
      >
        <Lead>How to obtain attested O/L and A/L certificates:</Lead>
        <List>
          <li>
            Follow the link below to obtain attestation of your original O/L and A/L certificates by
            the Ministry of Foreign Affairs, Sri Lanka.
          </li>
        </List>
        <LabelledLink label="Link:" href={DOENETS_URL} />
        <Note kind="Note">
          The attested O/L and A/L certificates will be sent to your email address as PDF documents
          within a few working days.
        </Note>
      </Step>

      <Step
        letter="C"
        title="Photocopies of the Attested O/L and A/L Certificates Certified by a Lawyer"
      >
        <Para>
          You must submit <strong>two photocopies of each O/L and A/L certificate</strong>,
          certified as true copies by a lawyer using an official English seal.
        </Para>
        <Lead>Instructions:</Lead>
        <List ordered>
          <li>
            Print three copies of each O/L and A/L certificate received as a PDF from the Ministry
            of Foreign Affairs (a total of six printouts: three O/L and three A/L).
          </li>
          <li>
            Take two printouts of each certificate to a lawyer to be certified as true copies using
            an official English seal (a total of four printouts to be certified).
          </li>
          <li>
            Keep the remaining printout of each certificate for submission as an original (a total
            of two printouts: one O/L and one A/L).
          </li>
        </List>
      </Step>
    </Section>

    <Section number={2} title="London O/L and A/L Certificates" chip="London">
      <Step letter="A" title="Original O/L and A/L Certificates issued by the UK Examination body">
        <Para>
          Ensure that you have your original London O/L and A/L certificates issued by the UK
          examination body.
        </Para>
      </Step>

      <Step
        letter="B"
        title="Original O/L and A/L Certificates attested by the British Council of Sri Lanka"
      >
        <Lead>Instructions:</Lead>
        <List ordered>
          <li>
            Make a one photocopy from each of your original London O/L and A/L certificates (a total
            of 2 photocopies).
          </li>
          <li>
            Visit the British Council of Sri Lanka and request attestation of these photocopies as
            true copies of your original certificates.
          </li>
          <li>Collect the photocopies once the process is complete.</li>
        </List>
        <Note kind="Important">
          The attestation process may take a few working days. Therefore, please initiate this
          process as soon as possible.
        </Note>
      </Step>

      <Step
        letter="C"
        title="Photocopies of the British Council-attested Certificates attested by a Lawyer"
      >
        <Para>
          You must obtain{' '}
          <strong>
            two photocopies of each O/L and A/L certificate attested by the British Council of Sri
            Lanka
          </strong>
          , with each photocopy further certified as a true copy by a lawyer using an official
          English seal.
        </Para>
        <Lead>Instructions:</Lead>
        <List>
          <li>
            Make 2 photocopies from each of your original British Council-attested O/L and A/L
            certificates (a total of 4 photocopies).
          </li>
          <li>
            Take the photocopies to a lawyer and request the lawyer to certify as true copies using
            an official English seal (a total of four photocopies to be certified).
          </li>
        </List>
      </Step>

      <Step
        letter="D"
        title="O/L and A/L Certificates Attested by the Ministry of Foreign Affairs, Sri Lanka"
      >
        <Lead>Instructions:</Lead>
        <List ordered>
          <li>
            Book an appointment with the Ministry of Foreign Affairs, Foreign Employment and Tourism
            through the e-Channeling portal using the link below.
            <LabelledLink label="Appointment link:" href={MFA_APPOINTMENT_URL} />
          </li>
          <li>
            Attend your appointment with the following documents:
            <List>
              <li>Original London O/L and A/L certificates.</li>
              <li>
                Original O/L and A/L Certificates attested by the British Council of Sri Lanka.
              </li>
              <li>
                One photocopy from each of your original O/L and A/L certificates attested by the
                British Council of Sri Lanka (total 2 photocopies).
              </li>
            </List>
          </li>
          <li>
            Submit the two British Council-attested photocopies to the Ministry of Foreign Affairs
            for attestation.
          </li>
        </List>
      </Step>

      <Step
        letter="E"
        title="Photocopies of the Ministry of Foreign Affairs-attested Certificates certified by a Lawyer"
      >
        <Para>
          You must submit{' '}
          <strong>
            two photocopies of each O/L and A/L certificate attested by the Ministry of Foreign
            Affairs
          </strong>
          , certified as true copies by a lawyer using an official English seal.
        </Para>
        <Lead>Instructions:</Lead>
        <List ordered>
          <li>
            Take three copies of each O/L and A/L certificate attested by the Ministry of Foreign
            Affairs (a total of six printouts: three O/L and three A/L).
          </li>
          <li>
            Take two printouts of each certificate to a lawyer to be certified as true copies using
            an official English seal (a total of four printouts to be certified).
          </li>
          <li>
            Keep the remaining printout of each certificate for submission as an original (a total
            of two printouts: one O/L and one A/L).
          </li>
        </List>
      </Step>
    </Section>

    <Note kind="Important Reminder">
      Please start the certification and attestation processes as early as possible, as some steps
      may take a few working days to complete.
    </Note>
  </Stack>
);
